import { useMemo, useState } from 'react';
import { ExpandableContentWrapper } from 'components/shared';
import { CanvasContentId, CanvasComponentConfig } from 'types/canvas';
import { ProductType } from 'types';
import { TraceConfig } from 'types/trace';
import { TraceShowChartType } from 'config/oi';
import { TraceProvider } from 'components/trace/TraceProvider';
import { getDefaultTraceConfig } from 'hooks/trace/useTraceConfig';
import { Trace } from 'pages/components/Trace';

interface CanvasTraceProps {
  id: number;
  config: CanvasComponentConfig;
  onConfigChange: (patch: Partial<CanvasComponentConfig>) => void;
}

const CANVAS_TRACE_SYM = 'SPX';

const CanvasTrace = ({
  id,
  config: canvasConfig,
  onConfigChange,
}: CanvasTraceProps) => {
  // `date` is deliberately omitted both when seeding from the saved Canvas
  // component config and when sending patches back to be persisted. The
  // chart should always default to today on every fresh load — saving
  // a date in the DB would mean reopening the tile tomorrow shows
  // yesterday's chart. Defaults from getDefaultTraceConfig() (which seeds
  // `date` to today) win.
  const [localConfig, setLocalConfig] = useState<TraceConfig>(() => {
    const { date: _date, ...savedRest } =
      (canvasConfig as Partial<TraceConfig>) ?? {};
    return { ...getDefaultTraceConfig(), ...savedRest };
  });

  // Persisted via Canvas config so the view survives the tile remount on resize.
  const [showChartType, setShowChartType] = useState<TraceShowChartType>(() => {
    const saved = (canvasConfig as { showChartType?: TraceShowChartType })
      ?.showChartType;
    return saved ?? TraceShowChartType.Both;
  });

  const config = useMemo<TraceConfig>(
    () => ({ ...localConfig, sym: CANVAS_TRACE_SYM }),
    [localConfig],
  );

  const handleConfigChange = (patch: Partial<TraceConfig>) => {
    setLocalConfig((prev) => ({ ...prev, ...patch }));
    const { date: _date, ...persistablePatch } = patch;
    if (Object.keys(persistablePatch).length > 0) {
      onConfigChange(persistablePatch as Partial<CanvasComponentConfig>);
    }
  };

  const handleShowChartTypeChange = (next: TraceShowChartType) => {
    setShowChartType(next);
    onConfigChange({ showChartType: next } as Partial<CanvasComponentConfig>);
  };

  const expandableType = useMemo(() => `${CanvasContentId.TRACE}-${id}`, [id]);

  return (
    <TraceProvider
      mode="canvas"
      config={config}
      onConfigChange={handleConfigChange}
      showChartType={showChartType}
      onShowChartTypeChange={handleShowChartTypeChange}
    >
      <ExpandableContentWrapper
        type={expandableType}
        // No padding: the tile already insets the component, and a second inset
        // here hid ~16px from the root height measurement and caused a scrollbar.
        styleOverrides={{ height: '100%' }}
      >
        <Trace productType={ProductType.INTERNAL_OPEN_INTEREST} />
      </ExpandableContentWrapper>
    </TraceProvider>
  );
};

export default CanvasTrace;
